import type { ReactNode } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export interface SlideOverSheetProps {
  /** Whether the sheet is currently shown to the user. On mobile (< md)
   * this gates the entire sheet: closed → no scrim + body hidden. On
   * desktop (md+) the body sits inline as a sibling column regardless of
   * `isOpen` (set `desktopGated` to flip that). */
  isOpen: boolean;
  /** Scrim click handler. Only fires on the mobile-overlay scrim. */
  onClose: () => void;
  /** Sheet content. The body wrapper handles its own positioning; pass
   * the actual content (e.g. <ScoutedList>) as children. */
  children: ReactNode;
  /** Optional aria-label for the dismiss scrim. */
  scrimAriaLabel?: string;
  /** When true, the body is also gated by `isOpen` on desktop (i.e. it
   * disappears when closed at every breakpoint). Default `false` —
   * desktop always shows the inline column so the layout doesn't reflow
   * when the mobile sheet toggles. */
  desktopGated?: boolean;
  /** Extra classes for the body wrapper. */
  className?: ClassName;
  /** Tracking testid prefix. The scrim gets `${testId}-scrim` and the
   * body gets `${testId}-body`. */
  testId?: string;
}

/**
 * Responsive right-anchored panel container — slide-over with scrim on
 * mobile, inline sibling column on desktop. Consolidates the two
 * near-identical wrappers that lived in App.tsx (Scout-mode scouted list +
 * Explore-mode trip detail). The scrim's darker tint stops the
 * underlying app chrome from bleeding through.
 *
 * On mobile (< md):
 *   - `isOpen = true`  → scrim renders + body slides over from right
 *   - `isOpen = false` → body hidden, no scrim
 *
 * On desktop (md+):
 *   - Body always renders inline as a sibling column (unless
 *     `desktopGated` is set, in which case it's gated by `isOpen` too).
 *   - Scrim never renders.
 */
export const SlideOverSheet = ({
  isOpen,
  onClose,
  children,
  scrimAriaLabel = 'Close panel',
  desktopGated = false,
  className,
  testId,
}: SlideOverSheetProps) => {
  // When desktop-gated and closed, render nothing — the panel is purely
  // ephemeral. Otherwise we always emit the body so the desktop layout
  // can keep it inline; mobile visibility is class-controlled.
  if (desktopGated && !isOpen) return null;
  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label={scrimAriaLabel}
          onClick={onClose}
          // md:hidden — desktop never shows the scrim, the panel is
          // an inline column there. The global `bg-content-scrim` token
          // was darkened (theme.css) so the app chrome behind the sheet
          // no longer reads as "still interactive."
          className="fixed inset-0 bg-content-scrim z-modal-scrim cursor-default md:hidden motion-safe:animate-kawaii-day-fade"
          data-testid={testId ? `${testId}-scrim` : undefined}
        />
      )}
      <div
        className={twx(
          // Desktop: inline relative column with intrinsic width so the
          // inner content (e.g. ScoutedList) decides its own size.
          'md:relative md:inset-auto md:z-auto md:w-auto md:flex md:animate-none order-3',
          // Mobile: when open, fixed right-anchored sheet. Below the `sm`
          // breakpoint (< 480px) the sheet fills the full viewport so
          // the underlying chrome doesn't peek around the edges on
          // narrow phones. From `sm` upward it caps at 420px wide so
          // the underlying map stays partially visible on roomier
          // phones. Closed → hidden.
          isOpen
            ? 'fixed inset-y-0 right-0 w-screen sm:w-[420px] z-modal flex motion-safe:animate-kawaii-row-enter'
            : 'hidden',
          className,
        )}
        data-testid={testId ? `${testId}-body` : undefined}
      >
        {children}
      </div>
    </>
  );
};
